export type SmePendingProject = {
  projectId?: number;
  ProjectId?: number;
  id?: number;
  userId?: number;
  UserId?: number;
  stateDateTime?: string;
  StateDateTime?: string;
  projectName?: string;
  username?: string;
  [key: string]: unknown;
};

export const getCompanyName = (project: SmePendingProject | null | undefined): string => {
  if (!project) return "";
  return String(project.companyName ?? project.CompanyName ?? "").trim();
};

export const getProjectName = (project: SmePendingProject | null | undefined): string => {
  if (!project) return "";
  return String(project.projectName ?? project.ProjectName ?? "").trim();
};

export const getProjectId = (project: SmePendingProject | null | undefined): number | null => {
  if (!project) return null;
  const id = project.projectId ?? project.ProjectId ?? project.id;
  if (id == null || id === "") return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Client / team-member user id — required for assessment, chat, and report APIs. */
export const getClientUserId = (project: SmePendingProject | null | undefined): number | null => {
  if (!project) return null;
  const id = project.userId ?? project.UserId;
  if (id == null || id === "") return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getPendingProjectKey = (project: SmePendingProject): string => {
  const projectId = getProjectId(project) ?? "unknown-project";
  const userId = getClientUserId(project) ?? "unknown-user";
  return `${projectId}:${userId}`;
};

/**
 * The pending-project API can return multiple rows per project (one per team member).
 * Deduplicate by project + client user, keeping the most recent submission.
 */
export const dedupePendingProjects = (projects: SmePendingProject[] = []): SmePendingProject[] => {
  const byKey = new Map<string, SmePendingProject>();

  for (const project of projects) {
    const key = getPendingProjectKey(project);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, project);
      continue;
    }

    const existingTime = new Date(
      (existing.stateDateTime ?? existing.StateDateTime ?? 0) as string | number
    ).getTime();
    const nextTime = new Date(
      (project.stateDateTime ?? project.StateDateTime ?? 0) as string | number
    ).getTime();

    if (nextTime >= existingTime) {
      byKey.set(key, project);
    }
  }

  return Array.from(byKey.values()).sort((a, b) => {
    const aTime = new Date((a.stateDateTime ?? a.StateDateTime ?? 0) as string | number).getTime();
    const bTime = new Date((b.stateDateTime ?? b.StateDateTime ?? 0) as string | number).getTime();
    return bTime - aTime;
  });
};

/** One row per project (most recent submission), for project-scoped flows such as AI reports. */
export const dedupePendingProjectsByProject = (
  projects: SmePendingProject[] = []
): SmePendingProject[] => {
  const byProjectId = new Map<number, SmePendingProject>();

  for (const project of projects) {
    const projectId = getProjectId(project);
    if (projectId == null) continue;

    const existing = byProjectId.get(projectId);
    if (!existing) {
      byProjectId.set(projectId, project);
      continue;
    }

    const existingTime = new Date(
      (existing.stateDateTime ?? existing.StateDateTime ?? 0) as string | number
    ).getTime();
    const nextTime = new Date(
      (project.stateDateTime ?? project.StateDateTime ?? 0) as string | number
    ).getTime();

    if (nextTime >= existingTime) {
      byProjectId.set(projectId, project);
    }
  }

  return Array.from(byProjectId.values()).sort((a, b) => {
    const aTime = new Date((a.stateDateTime ?? a.StateDateTime ?? 0) as string | number).getTime();
    const bTime = new Date((b.stateDateTime ?? b.StateDateTime ?? 0) as string | number).getTime();
    return bTime - aTime;
  });
};

export const getPendingProjectIdKey = (project: SmePendingProject): string => {
  const projectId = getProjectId(project);
  return projectId != null ? String(projectId) : "unknown-project";
};

const SME_PENDING_PROJECTS_CACHE_KEY = "reimagine_sme_pending_projects_v1";
const SME_PENDING_PROJECTS_CACHE_TTL_MS = 60 * 1000;

export const readCachedSmePendingProjects = (): SmePendingProject[] | null => {
  try {
    const raw = sessionStorage.getItem(SME_PENDING_PROJECTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { loadedAt: number; projects: SmePendingProject[] };
    if (!Array.isArray(parsed?.projects) || !parsed.loadedAt) return null;
    if (Date.now() - parsed.loadedAt > SME_PENDING_PROJECTS_CACHE_TTL_MS) {
      sessionStorage.removeItem(SME_PENDING_PROJECTS_CACHE_KEY);
      return null;
    }
    return parsed.projects;
  } catch {
    return null;
  }
};

export const writeCachedSmePendingProjects = (projects: SmePendingProject[]): void => {
  try {
    sessionStorage.setItem(
      SME_PENDING_PROJECTS_CACHE_KEY,
      JSON.stringify({ loadedAt: Date.now(), projects })
    );
  } catch {
    // ignore quota / private mode
  }
};
